import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Select as RadixSelect, Spinner } from '@radix-ui/themes';
import cn from 'classnames';

import { Text } from '../Text';
import { TextField } from '../TextField';
import { getFlyToRadixColor } from '../utils';
import styles from './Select.module.less';
import { SelectProps, sizeToTriggerClassName } from './Select.types';

function Select({
  color = 'accent',
  variant = 'surface',
  placeholder,
  options,
  size = '1',
  label,
  required,
  loading,
  searchable,
  searchPlaceholder = 'Search...',
  error,

  onChange,
  onValueChange,

  style,
  className,
  ref,
  ...props
}: SelectProps & { ref?: React.Ref<HTMLButtonElement> }) {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const radixColor = getFlyToRadixColor(color);
  const { t } = useTranslation();

  const filteredOptions = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const lowerSearch = search.toLowerCase();
    return options.filter((option) =>
      (option.textValue || String(option.value))
        .toLowerCase()
        .includes(lowerSearch)
    );
  }, [options, search, searchable]);

  useEffect(() => {
    if (!searchable || !inputRef.current) {
      return;
    }
    const element = inputRef.current as unknown as HTMLElement;
    const input =
      element.tagName === 'INPUT'
        ? (element as HTMLInputElement)
        : element.querySelector('input');

    if (input) {
      // 2. Use setTimeout to force focus AFTER Radix's internal focus management runs
      const timer = setTimeout(() => {
        input.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [filteredOptions, searchable]);

  const select = (
    <RadixSelect.Root
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
        e.stopPropagation()
      }
      onPointerDown={(e: React.PointerEvent<HTMLDivElement>) =>
        e.stopPropagation()
      }
      size={size}
      {...props}
      onValueChange={(v) => {
        onChange?.(v as any);
        onValueChange?.(v);
      }}
      onOpenChange={(open) => {
        if (!open) setSearch('');
        props.onOpenChange?.(open);
      }}
      disabled={props.disabled || loading}
    >
      <RadixSelect.Trigger
        {...props}
        color={radixColor}
        variant={variant}
        placeholder={placeholder}
        style={style}
        className={cn(
          className,
          sizeToTriggerClassName[size],
          loading && styles.loading,
          'rounded-4 font-semibold gap-fly-4',
          {
            'shadow-[inset_0_0_0_1px_var(--red-9)]': !!error,
          }
        )}
        ref={ref}
      >
        {loading ? <Spinner size='1' /> : undefined}
      </RadixSelect.Trigger>
      <RadixSelect.Content
        color={radixColor}
        className={cn('rounded-4', { 'max-h-60': searchable })}
        {...(searchable
          ? {
              position: 'popper',
              side: 'bottom',
            }
          : {})}
      >
        {searchable && (
          <div
            className='-top-2 -mt-2 left-0 p-2 border-gray-5 sticky z-10 border-b bg-(--color-panel-solid)'
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
              e.stopPropagation()
            }
            onPointerDown={(e: React.PointerEvent<HTMLDivElement>) =>
              e.stopPropagation()
            }
          >
            <TextField
              ref={inputRef}
              size={size}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        )}
        <div className='flex-1 overflow-y-auto'>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <RadixSelect.Item
                disabled={option.disabled}
                key={option.value}
                value={option.value}
                textValue={option.textValue}
              >
                {option.children ?? option.textValue}
              </RadixSelect.Item>
            ))
          ) : (
            <div className='px-6 py-2 text-sm text-center'>
              {t('app.common.not_found.not_found_text', {
                defaultValue: 'Not Found',
              })}
            </div>
          )}
        </div>
      </RadixSelect.Content>
    </RadixSelect.Root>
  );

  return label ? (
    <label className='flex flex-col gap-fly-4'>
      <Text size='2' highContrast weight='bold'>
        {label}
        {required && '*'}
      </Text>
      {select}
      {!!error && (
        <Text size='2' color='red'>
          {error}
        </Text>
      )}
    </label>
  ) : (
    select
  );
}

export { Select };
